import React, { useState } from "react";
import {
  BlockStack,
  Text,
  Button,
  Divider,
  Box,
  InlineStack,
  Icon,
  Badge,
} from "@medentic/polaris-react";
import {
  PlusCircleIcon,
  EditIcon,
  DuplicateIcon,
  PlusIcon,
  AlertTriangleIcon,
  DeleteIcon,
} from "@medentic/polaris-icons";
import { useTranslation } from "react-i18next";
import { AgendaSession } from "@features/events/types";
import { getLocalizedText } from "@shared/lib";
import { sessionsOverlap } from "./agendaUtils";
import InstructorsAvatarGroup from "@features/events/components/InstructorsAvatarGroup";

// ========================
// SESSION EMPTY STATE (Internal)
// ========================
interface SessionsEmptyStateProps {
  onAction?: () => void;
  actionLabel?: string;
}

const SessionsEmptyState: React.FC<SessionsEmptyStateProps> = ({
  onAction,
  actionLabel,
}) => {
  const { t } = useTranslation();

  return (
    <Box paddingBlock="300">
      <BlockStack gap="200">
        <Box padding="300">
          <Text as="p" variant="bodyMd" tone="subdued" alignment="center">
            {t("eventDetails.agenda.noSessions")}
          </Text>
        </Box>
        {onAction && (
          <InlineStack align="center">
            <Button
              icon={PlusIcon}
              variant="primary"
              size="slim"
              onClick={onAction}
            >
              {actionLabel || t("eventDetails.agenda.program.addButton")}
            </Button>
          </InlineStack>
        )}
      </BlockStack>
    </Box>
  );
};

// ========================
// SESSION COMPONENT (Internal)
// ========================
interface SessionProps {
  session: AgendaSession;
  index: number;
  onEdit: (session: AgendaSession, index: number) => void;
  onRemove: (index: number) => void;
  onDuplicate: (session: AgendaSession) => void;
  allSessions: AgendaSession[];
}

const Session: React.FC<SessionProps> = ({
  session,
  index,
  onEdit,
  onRemove,
  onDuplicate,
  allSessions,
}) => {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);

  // Check if current session overlaps with any other session
  const hasOverlap = allSessions.some(
    (otherSession, otherIndex) =>
      otherIndex !== index && sessionsOverlap(session, otherSession)
  );

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Box padding="200" borderRadius="200" background="bg-surface">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gap: "var(--p-space-400)",
            alignItems: "center",
          }}
        >
          <Box minWidth="0" width="100%">
            <BlockStack gap="150">
              <InlineStack gap="200" blockAlign="center" wrap={false}>
                <Box minWidth="90px">
                    <InlineStack gap="100" blockAlign="center" wrap={false}>
                        <Text as="span" variant="bodyMd" fontWeight="medium">
                            {session.from} - {session.to}
                        </Text>
                        {hasOverlap && (
                            <Icon source={AlertTriangleIcon} tone="warning"/>
                        )}
                    </InlineStack>
                </Box>
                <Box minWidth="0">
                  <Text
                    as="span"
                    variant="bodyMd"
                    fontWeight="regular"
                    truncate={true}
                  >
                    {getLocalizedText(session.title)}
                  </Text>
                </Box>

                {session.tags && session.tags.length > 0 && (
                  <div style={{ marginLeft: "auto" }}>
                    <InlineStack gap="100" wrap={false}>
                      {session.tags.map((tag, tagIndex) => (
                        <Badge key={tagIndex} tone="enabled" size="small">
                          {getLocalizedText(tag.label)}
                        </Badge>
                      ))}
                    </InlineStack>
                  </div>
                )}
              </InlineStack>

              {/* Instructors Row */}
              {session.instructorIds && session.instructorIds.length > 0 && (
                <InlineStack gap="100" blockAlign="center">
                  <InstructorsAvatarGroup
                    instructorIds={session.instructorIds}
                    maxShow={5}
                    size="small"
                  />
                </InlineStack>
              )}
            </BlockStack>
          </Box>

          <Box minWidth="90px" opacity={isHovered ? "1" : "0"}>
            <InlineStack gap="200" blockAlign="center" align="end" wrap={false}>
              <Button
                icon={EditIcon}
                variant="plain"
                size="slim"
                onClick={() => onEdit(session, index)}
                accessibilityLabel={t("eventDetails.agenda.editSession")}
              />
              <Button
                icon={DuplicateIcon}
                variant="plain"
                size="slim"
                onClick={() => onDuplicate(session)}
                accessibilityLabel={t("eventDetails.agenda.duplicateSession")}
              />
              <Button
                icon={DeleteIcon}
                variant="plain"
                size="slim"
                tone="critical"
                onClick={() => onRemove(index)}
                accessibilityLabel={t("eventDetails.agenda.removeSession")}
              />
            </InlineStack>
          </Box>
        </div>
      </Box>
    </div>
  );
};

// ========================
// SESSIONS LIST RENDERER (Exported)
// ========================
export interface SessionsListProps {
  sessions: AgendaSession[];
  dayIndex: number;
  swimlaneIndex: number;
  onEditSession: (session: AgendaSession, index: number) => void;
  onRemoveSession: (index: number) => void;
  onDuplicateSession: (session: AgendaSession) => void;
  onAddSession: () => void;
}

export const SessionsList: React.FC<SessionsListProps> = ({
  sessions,
  onEditSession,
  onRemoveSession,
  onDuplicateSession,
  onAddSession,
}) => {
  const { t } = useTranslation();

  if (sessions.length === 0) {
    return (
      <SessionsEmptyState
        onAction={onAddSession}
        actionLabel={t("eventDetails.agenda.program.addButton")}
      />
    );
  }

  return (
    <BlockStack gap="100">
      <Box>
        {sessions.map((session: AgendaSession, sessionIndex: number) => (
          <div key={sessionIndex}>
            <Session
              session={session}
              index={sessionIndex}
              onEdit={onEditSession}
              onRemove={onRemoveSession}
              onDuplicate={onDuplicateSession}
              allSessions={sessions}
            />
            {sessionIndex < sessions.length - 1 && <Divider />}
          </div>
        ))}
        {sessions.length > 0 && <Divider />}
      </Box>
      <Box padding="200">
        <Button variant="plain" icon={PlusCircleIcon} onClick={onAddSession}>
          {t("eventDetails.agenda.program.addButton")}
        </Button>
      </Box>
    </BlockStack>
  );
};

// Also export as SessionsListRenderer for backwards compatibility
export const SessionsListRenderer = SessionsList;
